// O motor dos campeonatos, sem banco: monta a tabela (pontos corridos, mata-mata ou grupos e mata-mata), registra as
// partidas de cada confronto, aplica o W.O., avança a chave e calcula a classificação. A API e a mesa usam as mesmas
// funções; o armazém guarda o resultado.

export type Formato = 'pontos' | 'mata' | 'grupos';
export type Lado = 'a' | 'b';
export type Fase = 'liga' | 'grupo' | 'mata';

export const NOMES_DOS_FORMATOS: Record<Formato, string> = { pontos: 'Pontos corridos', mata: 'Mata-mata', grupos: 'Grupos e mata-mata' };
export const TRIOS_MIN = 4;
export const TRIOS_MAX = 64;
export const TAMANHOS_DA_CHAVE = [4, 8, 16, 32, 64] as const;
export const PONTOS_POR_VITORIA = 3;
export const PONTOS_POR_DERROTA = 0;
export const PLACAR_DO_WO: readonly [number, number] = [12, 0]; // quem vence por W.O. faz 12 a 0
export const MINUTOS_PARA_WO = 15;
export const TRIOS_POR_GRUPO = 4;
export const CLASSIFICAM_POR_GRUPO = 2;

// Na chave: null é "a definir"; VAGO é ninguém (o adversário passa direto).
export const VAGO = '';

export interface PartidaDoJogo {
  vencedor: Lado;
  pontos: [number, number]; // a, b
  comBot: boolean; // alguém caiu e o bot jogou no lugar dele
  em: number;
}

export interface Jogo {
  id: string;
  fase: Fase;
  rodada: number; // contada do começo do campeonato: os grupos vêm antes da chave
  grupo: number | null; // 0, 1, 2... nos grupos
  posicao: number; // a ordem do confronto na rodada; na chave, o lugar dele
  a: string | null;
  b: string | null;
  melhorDe: 1 | 3;
  partidas: PartidaDoJogo[];
  vencedor: Lado | null;
  wo: Lado | 'ambos' | null; // quem perdeu por W.O.
  manual: boolean; // resultado lançado pelo organizador
}

export interface Opcoes { formato: Formato; returno: boolean; melhorDe: 1 | 3 }

// Quantos trios cada formato aceita. null: pode.
export function conferirQuantidade(formato: Formato, n: number): string | null {
  if (n < TRIOS_MIN) return `O campeonato começa com pelo menos ${TRIOS_MIN} trios.`;
  if (n > TRIOS_MAX) return `O campeonato vai até ${TRIOS_MAX} trios.`;
  if (formato === 'grupos' && (n % TRIOS_POR_GRUPO !== 0 || n < 2 * TRIOS_POR_GRUPO)) return 'Grupos e mata-mata pede 8, 12, 16... trios: grupos de 4.';
  return null;
}

export const tamanhoDaChave = (n: number): number => TAMANHOS_DA_CHAVE.find((t) => t >= n) ?? TRIOS_MAX;

// A ordem dos cabeças de chave: o 1º pega o último, e os dois primeiros só se encontram na final.
export function ordemDaChave(tamanho: number): number[] {
  let ordem = [1];
  while (ordem.length < tamanho) { const m = ordem.length * 2 + 1; ordem = ordem.flatMap((s) => [s, m - s]); }
  return ordem;
}

// Pontos corridos: todos contra todos pelo método do círculo. Com número ímpar, quem cai com a folga descansa.
export function rodizio(trios: readonly string[]): [string, string][][] {
  const FOLGA = '\u0000';
  const t = trios.length % 2 ? [...trios, FOLGA] : [...trios];
  const n = t.length, rodadas: [string, string][][] = [];
  for (let r = 0; r < n - 1; r++) {
    const jogos: [string, string][] = [];
    for (let i = 0; i < n / 2; i++) {
      const x = t[i]!, y = t[n - 1 - i]!;
      if (x !== FOLGA && y !== FOLGA) jogos.push(r % 2 === 0 || i > 0 ? [x, y] : [y, x]);
    }
    rodadas.push(jogos);
    t.splice(1, 0, t.pop()!);
  }
  return rodadas;
}

const jogo = (id: string, fase: Fase, rodada: number, grupo: number | null, posicao: number, a: string | null, b: string | null, melhorDe: 1 | 3): Jogo =>
  ({ id, fase, rodada, grupo, posicao, a, b, melhorDe, partidas: [], vencedor: null, wo: null, manual: false });

// A chave inteira: a primeira rodada com os cabeças de chave (passe livre para os primeiros da lista quando sobram
// vagas) e as rodadas seguintes a definir. Sem os cabeças (seeds null), a chave toda fica a definir: é a dos grupos.
function chave(seeds: readonly string[] | null, tamanho: number, primeiraRodada: number, melhorDe: 1 | 3, novoId: () => string): Jogo[] {
  const ordem = ordemDaChave(tamanho), out: Jogo[] = [];
  for (let i = 0; i < tamanho / 2; i++) {
    const lado = (s: number) => (!seeds ? null : s <= seeds.length ? seeds[s - 1]! : VAGO);
    out.push(jogo(novoId(), 'mata', primeiraRodada, null, i, lado(ordem[2 * i]!), lado(ordem[2 * i + 1]!), melhorDe));
  }
  for (let r = 1, n = tamanho / 4; n >= 1; r++, n /= 2) {
    for (let i = 0; i < n; i++) out.push(jogo(novoId(), 'mata', primeiraRodada + r, null, i, null, null, melhorDe));
  }
  return out;
}

// Os grupos em serpentina: 1º, 2º, 3º e 4º da lista caem em grupos diferentes.
export function grupos(trios: readonly string[]): string[][] {
  const g = trios.length / TRIOS_POR_GRUPO, out: string[][] = Array.from({ length: g }, () => []);
  trios.forEach((t, i) => {
    const pote = Math.floor(i / g), j = i % g;
    out[pote % 2 === 0 ? j : g - 1 - j]!.push(t);
  });
  return out;
}

// A tabela do campeonato, com os trios na ordem de inscrição.
export function montarTabela(trios: readonly string[], op: Opcoes, novoId: () => string): Jogo[] {
  const erro = conferirQuantidade(op.formato, trios.length);
  if (erro) throw new Error(erro);
  if (op.formato === 'pontos') {
    const turno = rodizio(trios);
    const todas = op.returno ? [...turno, ...turno.map((r) => r.map(([x, y]) => [y, x] as [string, string]))] : turno;
    return avancar(todas.flatMap((r, i) => r.map(([x, y], p) => jogo(novoId(), 'liga', i + 1, null, p, x, y, 1))), '');
  }
  if (op.formato === 'mata') return avancar(chave(trios, tamanhoDaChave(trios.length), 1, op.melhorDe, novoId), '');
  const gs = grupos(trios), out: Jogo[] = [];
  gs.forEach((g, gi) => rodizio(g).forEach((r, ri) => r.forEach(([x, y], p) => out.push(jogo(novoId(), 'grupo', ri + 1, gi, p, x, y, 1)))));
  const rodadasDosGrupos = TRIOS_POR_GRUPO - 1, classificados = gs.length * CLASSIFICAM_POR_GRUPO;
  out.push(...chave(null, tamanhoDaChave(classificados), rodadasDosGrupos + 1, op.melhorDe, novoId));
  return out;
}

export const decidido = (j: Jogo): boolean => j.vencedor !== null || j.wo === 'ambos' || (j.a === VAGO && j.b === VAGO);
export const real = (t: string | null): t is string => t !== null && t !== VAGO;
export const trioDo = (j: Jogo, l: Lado): string | null => (l === 'a' ? j.a : j.b);
export const outro = (l: Lado): Lado => (l === 'a' ? 'b' : 'a');

// Uma partida do confronto. Melhor de 3: vence quem fizer 2.
export function registrarPartida(j: Jogo, p: PartidaDoJogo): Jogo {
  if (decidido(j)) return j;
  const partidas = [...j.partidas, p], precisa = j.melhorDe === 3 ? 2 : 1;
  const de = (l: Lado) => partidas.filter((x) => x.vencedor === l).length;
  const vencedor: Lado | null = de('a') >= precisa ? 'a' : de('b') >= precisa ? 'b' : null;
  return { ...j, partidas, vencedor };
}

// W.O.: quem completou a mesa ganha; ninguém completou, os dois perdem; os dois completaram, o organizador decide.
export function aplicarWO(j: Jogo, presentes: { a: boolean; b: boolean }): Jogo | null {
  if (decidido(j) || j.partidas.length > 0 || (presentes.a && presentes.b)) return null;
  if (presentes.a) return { ...j, vencedor: 'a', wo: 'b' };
  if (presentes.b) return { ...j, vencedor: 'b', wo: 'a' };
  return { ...j, vencedor: null, wo: 'ambos' };
}

// O organizador lança o resultado à mão (com motivo, guardado fora daqui).
export function lancarResultado(j: Jogo, vencedor: Lado | null, wo: Lado | 'ambos' | null): Jogo {
  return { ...j, vencedor: wo === 'ambos' ? null : vencedor, wo, manual: true };
}

// O que cada trio somou num jogo decidido: [pontos feitos, pontos sofridos].
function placarDo(j: Jogo, l: Lado): [number, number] {
  if (j.wo === 'ambos') return [0, PLACAR_DO_WO[0]];
  if (j.wo) return l === j.wo ? [PLACAR_DO_WO[1], PLACAR_DO_WO[0]] : [PLACAR_DO_WO[0], PLACAR_DO_WO[1]];
  const meu = (p: PartidaDoJogo) => (l === 'a' ? p.pontos[0] : p.pontos[1]), deles = (p: PartidaDoJogo) => (l === 'a' ? p.pontos[1] : p.pontos[0]);
  return j.partidas.reduce<[number, number]>((s, p) => [s[0] + meu(p), s[1] + deles(p)], [0, 0]);
}

export interface LinhaDaClassificacao {
  trio: string; posicao: number; jogos: number; vitorias: number; derrotas: number; pontos: number; saldo: number; feitos: number;
}

// Número fixo por campeonato e trio: o sorteio do desempate, igual em toda leitura.
function sorteio(semente: string, trio: string): number {
  let h = 2166136261;
  for (const c of semente + ':' + trio) h = Math.imul(h ^ c.charCodeAt(0), 16777619) >>> 0;
  return h;
}

// Classificação de pontos corridos (ou de um grupo). Desempate: confronto direto, saldo de pontos das partidas,
// pontos feitos e sorteio.
export function classificar(trios: readonly string[], jogos: readonly Jogo[], semente: string): LinhaDaClassificacao[] {
  const linhas = new Map(trios.map((t) => [t, { trio: t, posicao: 0, jogos: 0, vitorias: 0, derrotas: 0, pontos: 0, saldo: 0, feitos: 0 }]));
  const validos = jogos.filter((j) => decidido(j) && real(j.a) && real(j.b) && linhas.has(j.a) && linhas.has(j.b));
  for (const j of validos) {
    for (const l of ['a', 'b'] as const) {
      const x = linhas.get(trioDo(j, l)!)!, [f, s] = placarDo(j, l);
      x.jogos++; x.feitos += f; x.saldo += f - s;
      if (j.vencedor === l) { x.vitorias++; x.pontos += PONTOS_POR_VITORIA; } else { x.derrotas++; x.pontos += PONTOS_POR_DERROTA; }
    }
  }
  const porPontos = new Map<number, LinhaDaClassificacao[]>();
  for (const x of linhas.values()) porPontos.set(x.pontos, [...(porPontos.get(x.pontos) ?? []), x]);
  const ordenadas: LinhaDaClassificacao[] = [];
  for (const pts of [...porPontos.keys()].sort((a, b) => b - a)) {
    const empatados = porPontos.get(pts)!, nomes = new Set(empatados.map((x) => x.trio));
    const direto = new Map(empatados.map((x) => [x.trio, 0]));
    for (const j of validos) {
      if (!nomes.has(j.a!) || !nomes.has(j.b!) || !j.vencedor) continue;
      const v = trioDo(j, j.vencedor)!;
      direto.set(v, direto.get(v)! + PONTOS_POR_VITORIA);
    }
    ordenadas.push(...empatados.sort((x, y) => direto.get(y.trio)! - direto.get(x.trio)! || y.saldo - x.saldo || y.feitos - x.feitos
      || sorteio(semente, x.trio) - sorteio(semente, y.trio)));
  }
  return ordenadas.map((x, i) => ({ ...x, posicao: i + 1 }));
}

// Os trios de cada grupo, na ordem em que aparecem nos jogos.
export function triosDosGrupos(jogos: readonly Jogo[]): string[][] {
  const out: string[][] = [];
  for (const j of jogos) {
    if (j.fase !== 'grupo' || j.grupo === null) continue;
    const g = (out[j.grupo] ??= []);
    for (const t of [j.a, j.b]) if (real(t) && !g.includes(t)) g.push(t);
  }
  return out;
}

// Depois de cada resultado: passe livre na chave, o vencedor sobe para o próximo confronto e, com os grupos fechados,
// os 2 primeiros de cada grupo entram na chave. Devolve a lista nova (os jogos que mudaram são cópias).
export function avancar(jogos: readonly Jogo[], semente: string): Jogo[] {
  const out = jogos.map((j) => ({ ...j }));
  const mata = (rodada: number, posicao: number) => out.find((j) => j.fase === 'mata' && j.rodada === rodada && j.posicao === posicao);
  const primeiraDaChave = Math.min(...out.filter((j) => j.fase === 'mata').map((j) => j.rodada));
  // Grupos fechados: a chave recebe 1º de cada grupo e depois os 2º.
  const doGrupo = out.filter((j) => j.fase === 'grupo');
  const primeira = out.filter((j) => j.fase === 'mata' && j.rodada === primeiraDaChave);
  if (doGrupo.length && doGrupo.every(decidido) && primeira.length && primeira.every((j) => j.a === null && j.b === null)) {
    const tabelas = triosDosGrupos(doGrupo).map((ts, g) => classificar(ts, doGrupo.filter((j) => j.grupo === g), semente + ':' + g));
    const seeds: string[] = [];
    for (let k = 0; k < CLASSIFICAM_POR_GRUPO; k++) for (const t of tabelas) seeds.push(t[k]!.trio);
    const ordem = ordemDaChave(primeira.length * 2);
    for (const j of primeira) {
      const s = (n: number) => (n <= seeds.length ? seeds[n - 1]! : VAGO);
      j.a = s(ordem[2 * j.posicao]!); j.b = s(ordem[2 * j.posicao + 1]!);
    }
  }
  for (let mudou = true; mudou;) {
    mudou = false;
    for (const j of out) {
      if (j.fase !== 'mata') continue;
      // Passe livre: o outro lado é ninguém.
      if (!decidido(j) && j.a !== null && j.b !== null && (j.a === VAGO || j.b === VAGO)) {
        if (j.a !== VAGO) j.vencedor = 'a'; else if (j.b !== VAGO) j.vencedor = 'b';
        mudou = true;
      }
      if (!decidido(j)) continue;
      const prox = mata(j.rodada + 1, Math.floor(j.posicao / 2));
      if (!prox) continue;
      const lado: Lado = j.posicao % 2 === 0 ? 'a' : 'b';
      const sobe = j.vencedor ? trioDo(j, j.vencedor)! : VAGO;
      if (trioDo(prox, lado) === null) { if (lado === 'a') prox.a = sobe; else prox.b = sobe; mudou = true; }
    }
  }
  return out;
}

// Reabrir um jogo decidido sem partida (W.O. ou resultado à mão), para remarcar ou corrigir. Só enquanto o resultado
// não andou: na chave, o próximo confronto sem partida e sem decisão; nos grupos, a chave ainda vazia. Devolve a lista
// nova ou o motivo da recusa.
export function reabrir(jogos: readonly Jogo[], id: string): Jogo[] | string {
  const out = jogos.map((j) => ({ ...j }));
  const j = out.find((x) => x.id === id);
  if (!j) return 'Esse jogo não existe.';
  if (!decidido(j)) return out;
  if (j.partidas.length) return 'Esse jogo já teve partida e não volta a ficar em aberto.';
  if (j.fase === 'grupo') {
    const primeira = Math.min(...out.filter((x) => x.fase === 'mata').map((x) => x.rodada));
    if (out.some((x) => x.fase === 'mata' && x.rodada === primeira && (x.a !== null || x.b !== null))) return 'Os grupos já fecharam e a chave já foi montada.';
  }
  if (j.fase === 'mata') {
    const prox = out.find((x) => x.fase === 'mata' && x.rodada === j.rodada + 1 && x.posicao === Math.floor(j.posicao / 2));
    if (prox) {
      if (prox.partidas.length || decidido(prox)) return 'O resultado deste jogo já andou na chave.';
      if (j.posicao % 2 === 0) prox.a = null; else prox.b = null;
    }
  }
  Object.assign(j, { vencedor: null, wo: null, manual: false });
  return out;
}

export const concluido = (jogos: readonly Jogo[]): boolean => jogos.length > 0 && jogos.every(decidido);

// O campeão: o vencedor da final ou o 1º dos pontos corridos. null enquanto não acabou (ou final sem vencedor).
export function campeao(jogos: readonly Jogo[], trios: readonly string[], semente: string): string | null {
  if (!concluido(jogos)) return null;
  const mata = jogos.filter((j) => j.fase === 'mata');
  if (mata.length) {
    const final = mata.reduce((f, j) => (j.rodada > f.rodada ? j : f));
    return final.vencedor ? trioDo(final, final.vencedor) : null;
  }
  return classificar(trios, jogos, semente)[0]?.trio ?? null;
}

// Quantas rodadas o campeonato tem (os grupos e a chave somados).
export const totalDeRodadas = (jogos: readonly Jogo[]): number => Math.max(0, ...jogos.map((j) => j.rodada));

// O nome da rodada da chave pelo número de confrontos: Final, Semifinal, Quartas...
export function nomeDaRodada(jogos: readonly Jogo[], rodada: number): string {
  const daRodada = jogos.filter((j) => j.rodada === rodada);
  if (!daRodada.length) return `Rodada ${rodada}`;
  if (daRodada[0]!.fase === 'grupo') return `Grupos, rodada ${rodada}`;
  if (daRodada[0]!.fase === 'liga') return `Rodada ${rodada}`;
  const n = daRodada.length;
  return ({ 1: 'Final', 2: 'Semifinal', 4: 'Quartas de final', 8: 'Oitavas de final', 16: 'Dezesseis-avos de final', 32: 'Trinta e dois avos de final' } as Record<number, string>)[n] ?? `Rodada ${rodada}`;
}
